import React, { useState, useEffect, useCallback } from 'react';
import { Phone, Calendar, Menu, X } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import { mockData } from '../mock';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [isScrolling, setIsScrolling] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleScroll = useCallback(() => {
    if (!isScrolling) {
      const sections = ['home', 'about', 'treatments', 'gallery', 'testimonials', 'offers', 'contact'];
      let currentSection = '';
      let minDistance = Infinity;

      sections.forEach(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          const distance = Math.abs(rect.top);
          if (distance < minDistance && rect.top <= 100) {
            minDistance = distance;
            currentSection = section;
          }
        }
      });

      if (currentSection !== activeSection) {
        setActiveSection(currentSection);
      }
    }
  }, [isScrolling, activeSection]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    // Initial check for active section
    setTimeout(handleScroll, 100);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleBookConsultation = () => {
    const message = `Hi! I'm interested in booking a consultation at ${mockData.clinic.name}.`;
    window.open(`https://wa.me/${mockData.clinic.whatsapp.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleCallNow = () => {
    window.open(`tel:${mockData.clinic.phone}`, '_self');
  };

  const handleNavClick = (sectionId) => {
    // Check if we're on the home page
    if (location.pathname !== '/') {
      // Navigate to home with scroll target
      navigate('/', { state: { scrollTo: `#${sectionId}` } });
      if (isMenuOpen) {
        toggleMenu();
      }
      return;
    }

    // We're on home page, proceed with normal scrolling
    setIsScrolling(true);
    setActiveSection(sectionId);

    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });

      // Reset scrolling state after animation completes
      setTimeout(() => {
        setIsScrolling(false);
      }, 1000); // Match this with your scroll animation duration
    }

    if (isMenuOpen) {
      toggleMenu();
    }
  };

  const NavLink = ({ sectionId, children }) => {
    const isActive = activeSection === sectionId;
    return (
      <button 
        onClick={() => handleNavClick(sectionId)}
        className={`nav-link ${isActive ? 'text-accent-sage' : ''}`}
      >
        {children}
      </button>
    );
  };

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50 border-b border-border-light">
      <div className="container mx-auto !px-2 !py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <button 
              onClick={() => handleNavClick('home')}
              className="focus:outline-none"
            >
              <img
                src="/images/logo-transperant.svg"
                alt="Clinic Logo"
                className="w-56 h-auto object-contain"
              />
            </button>         
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            <NavLink sectionId="about">About</NavLink>
            <NavLink sectionId="treatments">Treatments</NavLink>
            <NavLink sectionId="gallery">Gallery</NavLink>
            <NavLink sectionId="testimonials">Testimonials</NavLink>
            <NavLink sectionId="offers">Offers</NavLink>
            <NavLink sectionId="contact">Contact</NavLink>
          </nav>

          {/* Desktop CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <button
              onClick={handleCallNow}
              className="btn-secondary flex items-center space-x-2"
            >
              <Phone size={16} />
              <span className="ml-2">Call</span>
            </button>
            <button
              onClick={handleBookConsultation}
              className="bg-green-500 hover:bg-green-600 text-white font-semibold text-[16px] min-h-[48px] px-7 py-[12px] rounded-full shadow-md hover:shadow-lg transition-all duration-300 ease-in-out flex items-center"
            >
              <FaWhatsapp size={18} />
              <span className="ml-2">Whatsapp</span>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-bg-sage-light transition-colors"
            onClick={toggleMenu}
          >
            {isMenuOpen ? <X size={24} className="text-primary-sage" /> : <Menu size={24} className="text-primary-sage" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden mt-4 py-4 border-t border-border-light">
            <nav className="flex flex-col space-y-4">
              <NavLink sectionId="about">About</NavLink>
              <NavLink sectionId="treatments">Treatments</NavLink>
              <NavLink sectionId="gallery">Gallery</NavLink>
              <NavLink sectionId="testimonials">Testimonials</NavLink>
              <NavLink sectionId="offers">Offers</NavLink>
              <NavLink sectionId="contact">Contact</NavLink>
            </nav>
            <div className="flex flex-row justify-between mt-6 gap-3">
              <button
                onClick={handleCallNow}
                className="flex-1 btn-secondary flex items-center justify-center whitespace-nowrap px-4"
              >
                <Phone size={16} />
                <span className="ml-2">Call Now</span>
              </button>
              <button
                onClick={handleBookConsultation}
                className="flex-1 flex items-center justify-center whitespace-nowrap bg-green-500 hover:bg-green-600 text-white font-semibold text-[16px] px-4 py-[12px] rounded-full shadow-md hover:shadow-lg transition-all duration-300 ease-in-out"
              >
                <FaWhatsapp size={18} />
                <span className="ml-2">Whatsapp Now</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;